import React, { useState, useEffect } from "react";
import { Modal, ModalHeader } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { addPayment } from "common/services/payments.service";
import PaymentForm from "./PaymentForm";
import { toast } from "react-toastify";
import { extractFieldErrors } from "common/lib/fieldErrors";

const AddPayment: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchData: () => void;
}> = ({ toggle, isModalOpen, fetchData }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<any>([]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      projectOrFund: "",
      donorOrFund: "",
      staffId: "",
      isSalaryAligned: false,
      amountPerMonth: "",
      startDate: "",
      endDate: "",
      paymentIdentifier: "",
      dataProvidedBy: "",
      positionTitle: "",
      individual: {
        value: "",
        label: "",
      },
      mda: {
        value: "",
        label: "",
      },
      agency: {
        value: "",
        label: "",
      },
      positionType: {
        value: "",
        label: "",
      },
      paymentCategory: {
        value: "",
        label: "",
      },
      thematicArea: {
        value: "",
        label: "",
      },
      sector: {
        value: "",
        label: "",
      },
    },
    validationSchema: Yup.object({
      projectOrFund: Yup.string().required("Please enter project"),
      donorOrFund: Yup.string(),
      staffId: Yup.string(),
      amountPerMonth: Yup.number().required("Please enter amount per month"),
      startDate: Yup.string()
        .required("Please enter start date")
        .test(
          "is-before-end-date",
          "Start date must be before or equal to end date",
          function (value) {
            const { endDate } = this.parent;
            if (!value || !endDate) return true; // Skip validation if either date is empty

            const startDateObj = new Date(value);
            const endDateObj = new Date(endDate);

            return startDateObj <= endDateObj;
          }
        ),
      endDate: Yup.string().required("Please enter end date"),
      // paymentIdentifier: Yup.string().required(
      //   "Please enter payment identifier"
      // ),
      dataProvidedBy: Yup.string().required("Please enter data provided by"),
      positionTitle: Yup.string().required("Please enter position title"),
      individual: Yup.object().shape({
        value: Yup.number().required("Please select individual"),
      }),
      mda: Yup.object().shape({
        value: Yup.number().required("Please select MDA"),
      }),
      region: Yup.object().shape({
        value: Yup.number().required("Please select region"),
      }),
      agency: Yup.object().shape({
        value: Yup.number().required("Please select agency"),
      }),
      positionType: Yup.object().shape({
        value: Yup.number().required("Please select position type"),
      }),
      paymentCategory: Yup.object().shape({
        value: Yup.number().required("Please select payment category"),
      }),
      thematicArea: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
      sector: Yup.object().shape({
        value: Yup.number().nullable(),
      }),
    }),
    onSubmit: async (values: any) => {
      const data = {
        data: {
          individual: values.individual?.value || null,
          mda: values.mda?.value || null,
          agency: values.agency?.value || null,
          positionType: values.positionType?.value || null,
          paymentCategory: values.paymentCategory?.value || null,
          projectOrFund: values.projectOrFund
            ? values.projectOrFund.trim()
            : null,
          donorOrFund: values.donorOrFund
            ? values.donorOrFund.trim()
            : null,
          staffId: values.staffId ? values.staffId.trim() : null,
          isSalaryAligned: values.isSalaryAligned,
          amountPerMonth: values.amountPerMonth || null,
          startDate:
            (values.startDate &&
              values.startDate.toLocaleDateString("en-CA")) ||
            null,
          endDate:
            (values.endDate && values.endDate.toLocaleDateString("en-CA")) ||
            null,
          // paymentIdentifier: values.paymentIdentifier,
          dataProvidedBy: values.dataProvidedBy
            ? values.dataProvidedBy.trim()
            : null,
          positionTitle: values.positionTitle
            ? values.positionTitle.trim()
            : null,
          thematicArea: values.thematicArea?.value || null,
          sector: values.sector?.value || null,
        },
      };

      setLoading(true);
      try {
        await addPayment(data);
        fetchData();
        validation.resetForm();
        toggle();
        toast.success("Payment added successfully", {});
      } catch (e: any) {
        const errors = extractFieldErrors(e?.response);
        setFieldErrors(errors);
        toast.error("Payment creation failed", {});
      } finally {
        setLoading(false);
      }
    },
  });

  // Update fieldErrors when validation errors change
  useEffect(() => {
    const validationErrors = [];

    // Convert Formik validation errors to fieldErrors format
    for (const [fieldName, errorMessage] of Object.entries(validation.errors)) {
      if (validation.touched[fieldName] && errorMessage) {
        // Handle nested object errors (like individual.value, mda.value, etc.)
        if (typeof errorMessage === "object" && errorMessage !== null) {
          for (const [nestedField, nestedError] of Object.entries(
            errorMessage
          )) {
            if (nestedError) {
              validationErrors.push({
                field: `${fieldName}.${nestedField}`,
                message: nestedError,
              });
            }
          }
        } else {
          validationErrors.push({
            field: fieldName,
            message: errorMessage,
          });
        }
      }
    }

    setFieldErrors(validationErrors);
  }, [validation.errors, validation.touched]);

  return (
    <Modal
      id="showModal"
      size="xl"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        Add Payment
      </ModalHeader>

      <PaymentForm
        validation={validation}
        isUpdate={false}
        loading={loading}
        toggle={toggle}
        fieldErrors={fieldErrors}
      />
    </Modal>
  );
};

export default AddPayment;
